
const AISolutionsPage = () => {
  const solutions = [
    {
      title: "AI-Powered Solar Street Lights",
      description: "Military-licensed smart lighting systems with integrated surveillance and drone capabilities",
      features: [
        "Solar-powered operation",
        "Integrated drone detection",
        "Smart grid connectivity",
        "Remote monitoring",
        "Weather-resistant design",
        "Military-grade encryption"
      ],
      specs: [
        "Operating Range: 500m radius",
        "Battery Life: 72 hours backup",
        "Detection Accuracy: 99.7%",
        "Response Time: <2 seconds"
      ],
      image: "https://public.readdy.ai/ai/img_res/8fddc04dfb552be3469db73cb96fd250.jpg"
    },
    {
      title: "Facial Recognition Systems",
      description: "Advanced biometric identification with real-time threat assessment capabilities",
      features: [
        "Real-time identification",
        "Threat database integration",
        "Multi-angle detection",
        "Low-light performance",
        "Privacy compliance",
        "API integration"
      ],
      specs: [
        "Recognition Speed: 0.3 seconds",
        "Accuracy Rate: 99.9%",
        "Database Capacity: 10M+ faces",
        "Concurrent Processing: 1000+ faces"
      ],
      image: "https://readdy.ai/api/search-image?query=Advanced%20facial%20recognition%20system%20interface%2C%20biometric%20scanning%20technology%2C%20security%20monitoring%20screens%2C%20high-tech%20surveillance%20equipment%2C%20professional%20security%20technology%2C%20realistic%20AI%20interface&width=600&height=400&seq=facial-recognition&orientation=landscape"
    },
    {
      title: "Gunshot Detection Network",
      description: "Acoustic sensor network for immediate gunshot detection and location triangulation",
      features: [
        "Acoustic triangulation",
        "Instant alerts",
        "False positive filtering",
        "Integration with emergency services",
        "Weather compensation",
        "Caliber identification"
      ],
      specs: [
        "Detection Range: 2km radius",
        "Location Accuracy: ±5 meters",
        "Response Time: <1 second",
        "False Positive Rate: <0.1%"
      ],
      image: "https://readdy.ai/api/search-image?query=Acoustic%20gunshot%20detection%20sensors%20and%20monitoring%20system%2C%20security%20technology%20equipment%2C%20professional%20surveillance%20network%2C%20realistic%20security%20monitoring%20setup%2C%20high-tech%20detection%20systems&width=600&height=400&seq=gunshot-detection&orientation=landscape"
    },
    {
      title: "Drone Detection & Response",
      description: "Comprehensive UAV detection, tracking, and countermeasure deployment system",
      features: [
        "Multi-spectrum detection",
        "Automated tracking",
        "Threat classification",
        "Countermeasure deployment",
        "Flight path prediction",
        "Operator location"
      ],
      specs: [
        "Detection Range: 5km",
        "Tracking Accuracy: 99.5%",
        "Response Time: <3 seconds",
        "Simultaneous Targets: 50+"
      ],
      image: "https://readdy.ai/api/search-image?query=Drone%20detection%20and%20countermeasure%20system%2C%20anti-UAV%20technology%2C%20security%20radar%20equipment%2C%20professional%20drone%20defense%20system%2C%20military-grade%20detection%20technology%2C%20realistic%20security%20equipment&width=600&height=400&seq=drone-detection&orientation=landscape"
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white pt-16">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">
            AI Defense & <span className="text-yellow-500">Surveillance</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
            Cutting-edge artificial intelligence solutions for modern security challenges with military-grade technology
          </p>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {solutions.map((solution, index) => (
              <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <h2 className="text-3xl font-bold mb-6 text-white">
                    {solution.title}
                  </h2>
                  <p className="text-lg text-gray-400 mb-8 leading-relaxed">
                    {solution.description}
                  </p>

                  {/* Features */}
                  <div className="mb-8">
                    <h3 className="text-xl font-bold mb-4 text-yellow-500">Key Features</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {solution.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center">
                          <div className="w-2 h-2 bg-yellow-500 rounded-full mr-3"></div>
                          <span className="text-gray-300 text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Specifications */}
                  <div className="mb-8">
                    <h3 className="text-xl font-bold mb-4 text-yellow-500">Technical Specifications</h3>
                    <div className="space-y-2">
                      {solution.specs.map((spec, specIndex) => (
                        <div key={specIndex} className="flex items-center">
                          <i className="ri-check-line text-yellow-500 mr-3"></i>
                          <span className="text-gray-300 text-sm">{spec}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <button className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-bold py-3 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer">
                    Request Technical Details
                  </button>
                </div>

                <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                  <div className="relative">
                    <img
                      src={solution.image}
                      alt={solution.title}
                      className="rounded-xl shadow-2xl w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-xl"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Stats */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6 text-white">
              Military-Grade <span className="text-yellow-500">Performance</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Our AI systems deliver unmatched reliability and precision in critical security operations
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-yellow-500 mb-2">99.9%</div>
              <div className="text-gray-400">System Uptime</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-yellow-500 mb-2">&lt;1s</div>
              <div className="text-gray-400">Response Time</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-yellow-500 mb-2">24/7</div>
              <div className="text-gray-400">Monitoring</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-yellow-500 mb-2">5km</div>
              <div className="text-gray-400">Detection Range</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-gray-900 via-black to-gray-900">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-6 text-white">
            Ready to Deploy <span className="text-yellow-500">AI Security?</span>
          </h2>
          <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
            Contact our technical team to discuss implementation of our AI-powered security solutions for your facility.
          </p>
          <button className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-bold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer">
            Schedule Technical Consultation
          </button>
        </div>
      </section>
    </div>
  );
};

export default AISolutionsPage;
